import { AfterViewInit, Component, ViewEncapsulation } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FormComponentLookup, FormEntryComponentClass, validatorLookupRegistry } from '@ptl/forms';
import { PersonalInfoStateStructure } from '../../../shared/interfaces/personalInfoStateStructure.interface';
import { EnrollmentStateStructure } from '../../../shared/interfaces/enrollmentStateStructure.interface';
import { DateService } from '../../../../shared/services/date.service';
import { HttpHeaders } from '@angular/common/http';
import { BrandingConfigurationService } from '@ptl/brand';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { PtlNotificationService, PtlNotificationStyle } from '@ptl/global-components';

@FormComponentLookup('form-medicare-info-validate')
@Component({
    selector: 'app-medicare-info-validator',
    templateUrl: './medicare-info-validator.component.html',
    styleUrls: ['./medicare-info-validator.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class MedicareInfoValidatorComponent extends FormEntryComponentClass implements AfterViewInit {

  isLoading = false;
  isValid = false;
  hasAttemptedValidation = false;
  isCheckboxChecked = false;

  NETWORK_ERROR = 'Network Error';
  responseMessage: string;

  // Keeps track of previous state values to see if we've changed any data since our last validate
  prevStateValues: any = {};

  constructor(
    private ptlNotificationService: PtlNotificationService,
    private httpClient: HTTPWrapperContext,
    private brandService: BrandingConfigurationService,
    private dateService: DateService) {
    super();
  }

  ngOnInit() {
    // Listen for form changes to update the validity of this component - any change will require revalidation
    this.subscriptionGroup.push(this.parentForm['enrollment'].valueChanges.subscribe(() => {
      this.updateValidity()
    }));
    this.subscriptionGroup.push(this.parentForm['personalInfo'].valueChanges.subscribe(() => {
      this.updateValidity()
    }));
  }

  ngAfterViewInit() {
    const formControl: AbstractControl = this.formControls[this.entry.formControlAttributes.formControlName];
    
    formControl.setValidators((control: AbstractControl) => {
      let isCheckboxValid = this.isValid || this.isCheckboxChecked;
      return (!this.entry.base.display || (this.hasAttemptedValidation && isCheckboxValid)) ? null : {'invalidMedicareInfo': true};
    });

    formControl.updateValueAndValidity();
    this.subscribeToFormStream(this.entry.base.formListeners);
  }

  isDataAvailableAndValid() {
    const enrollment: EnrollmentStateStructure = this.reducerUtilityService.getPayloadStates().enrollment;
    const personalInfo: PersonalInfoStateStructure = this.reducerUtilityService.getPayloadStates().personalInfo;
    
    let isInfoPopulated = 
      personalInfo &&
      personalInfo.firstName &&
      personalInfo.lastName &&
      personalInfo.dob &&
      personalInfo.gender &&
      enrollment &&
      enrollment.medicareNumber;

    return isInfoPopulated && this.parentForm['enrollment'].valid && this.parentForm['personalInfo'].valid;
  }

  updateValidity() {
    const enrollment: EnrollmentStateStructure = this.reducerUtilityService.getPayloadStates().enrollment;
    const personalInfo: PersonalInfoStateStructure = this.reducerUtilityService.getPayloadStates().personalInfo;

    if (
      this.prevStateValues.firstName !== personalInfo.firstName ||
      this.prevStateValues.lastName !== personalInfo.lastName ||
      this.prevStateValues.dob !== personalInfo.dob ||
      this.prevStateValues.gender !== personalInfo.gender ||
      this.prevStateValues.medicareNumber !== enrollment.medicareNumber
    ) {
      this.hasAttemptedValidation = false;
      this.isValid = false;
    }
    
    this.prevStateValues = {
      firstName: personalInfo.firstName,
      lastName: personalInfo.lastName,
      dob: personalInfo.dob,
      gender: personalInfo.gender,
      medicareNumber: enrollment.medicareNumber
    };
      
    const formControl: AbstractControl = this.formControls[this.entry.formControlAttributes.formControlName];
    formControl.updateValueAndValidity();
  }

  async validateMedicareInfo() {
    const enrollment: EnrollmentStateStructure = this.reducerUtilityService.getPayloadStates().enrollment;
    const personalInfo: PersonalInfoStateStructure = this.reducerUtilityService.getPayloadStates().personalInfo;

    let payload = {
      mbi: enrollment.medicareNumber,
      birthDate: this.dateService.getDateAsFormattedString(personalInfo.dob, 'MM/DD/YYYY'),
      firstName: personalInfo.firstName,
      lastName: personalInfo.lastName,
      middleInitial: personalInfo.middleInitial,
      gender: personalInfo.gender
    };
    
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/sxesvc/api/v1/marx/validation',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: payload,
    });

    this.hasAttemptedValidation = false;
    this.isLoading = true;

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    this.httpClient.makeRequest(requestConfig)
    .then((response: {message: string}) =>{
      this.responseMessage = response ? response.message : '';

      if (this.responseMessage == 'Applicant details match') {
        this.isValid = true;
      }
      else {
        this.isValid = false;
      }
    })
    .catch((error) => {
      this.responseMessage = this.NETWORK_ERROR;
      this.isValid = false;
    })
    // We show the messages in the finally block to make sure we capture any network errors
    // and still allow the user to continue
    .finally(() => {
      this.hasAttemptedValidation = true;
      this.isLoading = false;

      if (this.isValid) {
        this.ptlNotificationService.open({
          title: 'Your entry is successful. We found your information!',
          content: '',
          primaryButtonText: 'Continue',
          notificationStyle: PtlNotificationStyle.SUCCESS
        });
      }
      else {
        this.ptlNotificationService.open({
          title: 'Oops! We could not find you in the system!',
          content: this.getErrorMessageContent(),
          primaryButtonText: 'Review Entries',
          notificationStyle: PtlNotificationStyle.WARNING
        });
      }
      
      const formControl: AbstractControl = this.formControls[this.entry.formControlAttributes.formControlName];
      formControl.updateValueAndValidity();
    });
  }

  checkboxChecked($event) {
    this.isCheckboxChecked = $event.checked;
      
    const formControl: AbstractControl = this.formControls[this.entry.formControlAttributes.formControlName];
    formControl.updateValueAndValidity();
  }

  getErrorMessageContent() {
    if (this.responseMessage === this.NETWORK_ERROR) {
      return 'A network error occurred. You can continue to enroll, but we cannot validate your information at this time.';
    }
    else {
      return this.responseMessage + '. You can continue to enroll, but check your Medicare card to make sure the information matches exactly.';
    }
  }
}
